import { FormFieldTitle } from "features/tradingTerminal/common/styles.ts";
import React, { useContext, useEffect, useMemo } from "react";
import Select from "common/Select/Select.tsx";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context.tsx";
import IconWithFallback from "Global/IconWithFallback.tsx";
import Text from "common/Typography/Text.tsx";
import Flex from "common/Boxes/Flex.tsx";
import { SubGroup, LanguageNameSpaces } from "@shared/types";
import { checkSearch } from "@shared/utils/misc.ts";
import { ErrorText } from "features/bots/common/styles.ts";
import { ErrorTypes } from "@shared/features/trades/common/types.ts";
import MissingGroupButton from "features/configuration/AccountsAndGroups/MissingGroupButton.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import CreatePersonalGroupButton from "features/bots/Groups/CreatePersonalGroupButton.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import useSignalsSubGroupSelector from "features/tradingTerminal/General/useSignalsSubGroupSelector.tsx";
import { useSelectUser } from "@shared/utils/hooks/misc.ts";
import { useTheme } from "styled-components";

const SubGroupSelector = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const {
    subGroupIds,
    setSubGroupIds,
    getGroupsDataQuery,
    getAdminSubGroupsStatus,
    generalErrors,
  } = useContext(TradingTerminalContext)!;
  const adminSubGroups = getAdminSubGroupsStatus?.data;
  const personalSubGroups = useMemo(
    () => getGroupsDataQuery?.data?.filter((subGroup) => subGroup.is_personal),
    [getGroupsDataQuery?.data]
  );
  const user = useSelectUser();
  const { isNewLook } = useTheme();
  const subGroups = adminSubGroups ?? personalSubGroups;

  const { onSubGroupChange, isAdminWorkspace, adminSelectorSubGroupId } =
    useSignalsSubGroupSelector({
      subGroupId: subGroupIds?.[0],
      subGroups,
      setSubGroupId: (subGroupId) => setSubGroupIds([subGroupId]),
    });

  useEffect(() => {
    if (subGroups && !subGroupIds?.length) {
      const defaultSubGroupId = adminSelectorSubGroupId ?? subGroups[0]?.id;
      setSubGroupIds(defaultSubGroupId ? [defaultSubGroupId] : []);
    }
  }, [subGroups]);

  const filterOption = (input: string, option?: { subGroup: SubGroup }) => {
    return checkSearch(option?.subGroup.name, input);
  };

  const options = useMemo(() => {
    return subGroups?.map((subGroup) => {
      return {
        value: subGroup.id,
        label: (
          <Flex $justifyContent="flex-start" style={{ overflow: "hidden" }}>
            <IconWithFallback
              iconLink={subGroup.image}
              iconStyle={{ marginRight: 8, borderRadius: 12 }}
              pxIconSize={18}
            />
            <Text $ellipsis={true}>{subGroup.name}</Text>
          </Flex>
        ),
        subGroup,
      };
    });
  }, [subGroups]);

  const error = generalErrors[ErrorTypes.SUB_GROUP];
  const missingGroupButton = user?.isWhopExperience() ? undefined : (
    <MissingGroupButton isLink={true} />
  );

  return (
    <div>
      <FormFieldTitle style={{ justifyContent: "space-between" }}>
        {t("labels.group")}
        {isAdminWorkspace ? (
          missingGroupButton
        ) : (
          <CreatePersonalGroupButton
            isLink={true}
            style={{ fontSize: typography.fontSizes.S }}
          />
        )}
      </FormFieldTitle>
      <Select
        showSearch={{ filterOption }}
        style={{ width: "100%" }}
        value={subGroupIds?.[0]}
        onChange={onSubGroupChange}
        popupMatchSelectWidth={false}
        optionLabelProp="label"
        options={options}
        mobileTitle={t("labels.group")}
        status={isNewLook && error ? "error" : undefined}
      />
      {!!error && <ErrorText>{error}</ErrorText>}
    </div>
  );
};

export default SubGroupSelector;
